import type { Types } from "@cornerstonejs/tools";
import { chaikinSmooth, extractContourEdges } from "./contourUtils";
import { getCornerstone, getCornerstoneTools } from "./dicomLoader";
import { LOADED_FROM_MASK_KEY } from "./annotationFlags";
import { initContourSegmentIndices } from "./niftiMaskSegmentInit";
import { ensureContourSegmentationActive, getContourSegmentationId } from "./viewportToolSetup";
import {
  decodePredictionMaskRuns,
  type ResolvedPredictionMask,
  type ResolvedPredictionMaskSegment,
  type ResolvedPredictionMaskSlice,
} from "../predictions/segmentationMask";
import { ACTIVE_SEGMENT_COLOR, setSegmentColor } from "./segmentationConstants";
import { copyContourForCheckpoint } from "./segmentationGeometryCheckpoint";

type Point2 = [number, number];
type Point3 = [number, number, number];

export interface PredictionMaskOverlayState {
  /** Slice key / contour UUID -> viewer-local segment index, not producer identity. */
  loadedKeys: Map<string, number>;
  annotationIds: Map<string, number>;
  /** Handed-off geometry must not be rebuilt from the immutable prediction. */
  detachedSegmentIndices: Set<number>;
  /** Problems already reported, so polling doesn't repeat a warning. */
  warnedKeys: Set<string>;
}

export function createPredictionMaskOverlayState(): PredictionMaskOverlayState {
  return {
    loadedKeys: new Map(),
    annotationIds: new Map(),
    detachedSegmentIndices: new Set(),
    warnedKeys: new Set(),
  };
}

/**
 * Relinquish a segment without deleting, unlocking, or modifying its contours.
 * The editor must capture pre-edit geometry before handoff. Detachment survives
 * overlay recovery; the caller's Cancel/Save lifecycle owns the exit policy.
 */
export function detachPredictionMaskSegment(
  state: PredictionMaskOverlayState,
  segmentIndex: number,
): void {
  state.detachedSegmentIndices.add(segmentIndex);
  for (const [key, owner] of state.loadedKeys) {
    if (owner === segmentIndex) state.loadedKeys.delete(key);
  }
  for (const [uid, owner] of state.annotationIds) {
    if (owner === segmentIndex) state.annotationIds.delete(uid);
  }
}

/** An edit-entry checkpoint for Cancel, separate from per-operation Undo. */
export interface PredictionMaskEditSnapshot {
  /** Viewer-local index of the geometry being edited. */
  segmentIndex: number;
  /** Viewer-local index whose prediction overlay ownership is handed off. */
  predictionSegmentIndex: number;
  segmentationVolumeId: string;
  contours: Types.Annotation[];
  labelmapVoxelIndices: Uint32Array;
  labelmapLength: number;
  overlayLoadedKeys: string[];
  overlayAnnotationIds: string[];
  wasDetached: boolean;
}

/** Read/copy only. The caller must capture successfully BEFORE detach/unlock. */
export function capturePredictionMaskEditSnapshot(
  state: PredictionMaskOverlayState,
  segmentationVolumeId: string,
  segmentIndex: number,
  predictionSegmentIndex: number = segmentIndex,
): PredictionMaskEditSnapshot {
  // Match the Panel's single-case, local-segment selection across native planes.
  // Capture live contours, not raw RLE: a candidate may already have been edited.
  const contours = getCornerstoneTools()
    .annotation.state.getAllAnnotations()
    .filter((ann) => {
      const segmentation = ann.data?.segmentation as { segmentIndex?: number } | undefined;
      return (
        ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
        segmentation?.segmentIndex === segmentIndex &&
        (ann.data?.contour?.polyline?.length ?? 0) >= 3
      );
    });
  if (contours.length === 0) throw new Error("Prediction contours are not ready for editing");

  const volume = getCornerstone().cache.getVolume(segmentationVolumeId);
  const scalarData = volume?.voxelManager?.getCompleteScalarDataArray?.();
  if (!scalarData) throw new Error("Prediction labelmap is not ready for an edit checkpoint");
  const voxelIndices: number[] = [];
  for (let i = 0; i < scalarData.length; i++) {
    if (scalarData[i] === segmentIndex) voxelIndices.push(i);
  }

  return {
    segmentIndex,
    predictionSegmentIndex,
    segmentationVolumeId,
    // Deep-copy nested geometry/flags while preserving Cornerstone's runtime
    // interpolation callback by reference. Functions cannot be structured-cloned.
    contours: contours.map(copyContourForCheckpoint),
    labelmapVoxelIndices: Uint32Array.from(voxelIndices),
    labelmapLength: scalarData.length,
    overlayLoadedKeys: [...state.loadedKeys]
      .filter(([, owner]) => owner === predictionSegmentIndex)
      .map(([key]) => key),
    overlayAnnotationIds: [...state.annotationIds]
      .filter(([, owner]) => owner === predictionSegmentIndex)
      .map(([uid]) => uid),
    wasDetached: state.detachedSegmentIndices.has(predictionSegmentIndex),
  };
}

/**
 * Restore one candidate's edit-entry checkpoint.
 * The caller must validate study/volume/session identity before calling.
 * Throws on failure; the caller must retain the checkpoint and Edit state.
 * This is not transactional: a runtime write failure may leave a partial restore.
 */
export function restorePredictionMaskEditSnapshot(
  state: PredictionMaskOverlayState,
  snapshot: PredictionMaskEditSnapshot,
): void {
  const csTools = getCornerstoneTools();
  const annotationState = csTools.annotation.state;
  const { segmentIndex, predictionSegmentIndex, segmentationVolumeId } = snapshot;
  const volume = getCornerstone().cache.getVolume(segmentationVolumeId);
  const voxelManager = volume?.voxelManager;
  const scalarData = voxelManager?.getCompleteScalarDataArray?.();

  // Validate and clone before removing live geometry.
  if (!voxelManager || !scalarData || scalarData.length !== snapshot.labelmapLength) {
    throw new Error("Prediction labelmap no longer matches the checkpoint");
  }
  const restoredContours = snapshot.contours.map(copyContourForCheckpoint);
  for (const ann of restoredContours) {
    const segmentation = ann.data.segmentation as
      | { segmentationId: string; segmentIndex: number }
      | undefined;
    if (
      !ann.annotationUID ||
      !ann.metadata?.FrameOfReferenceUID ||
      segmentation?.segmentIndex !== segmentIndex ||
      !csTools.segmentation.state.getSegmentation(segmentation.segmentationId)?.segments[
        segmentIndex
      ]
    ) {
      throw new Error("Prediction contour checkpoint is not ready to restore");
    }
  }
  for (const index of snapshot.labelmapVoxelIndices) {
    if (
      index >= scalarData.length ||
      (scalarData[index] !== 0 && scalarData[index] !== segmentIndex)
    ) {
      throw new Error("Prediction checkpoint conflicts with another segment");
    }
  }

  const currentContours = annotationState.getAllAnnotations().filter((ann) => {
    const segmentation = ann.data?.segmentation as { segmentIndex?: number } | undefined;
    return (
      ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
      segmentation?.segmentIndex === segmentIndex
    );
  });

  // Remove all current contours for this candidate, including prediction
  // contours edited in place and replacement contours created by AI.
  for (const ann of currentContours) {
    csTools.utilities.contourSegmentation.removeContourSegmentationAnnotation(
      ann as Types.ContourSegmentationAnnotation,
    );
    csTools.annotation.visibility.setAnnotationVisibility(ann.annotationUID!, true);
    csTools.annotation.locking.setAnnotationLocked(ann.annotationUID!, false);
  }
  for (const ann of currentContours) {
    annotationState.removeAnnotation(ann.annotationUID!);
  }

  for (const ann of restoredContours) {
    const locked = ann.isLocked;
    const visible = ann.isVisible;
    annotationState.addAnnotation(ann, ann.metadata!.FrameOfReferenceUID!);
    csTools.utilities.contourSegmentation.addContourSegmentationAnnotation(
      ann as Types.ContourSegmentationAnnotation,
    );
    csTools.annotation.locking.setAnnotationLocked(ann.annotationUID!, locked);
    csTools.annotation.visibility.setAnnotationVisibility(ann.annotationUID!, visible);
  }

  // getCompleteScalarDataArray may return a copy.
  // Write through the voxel manager, preserving other segment values.
  for (let index = 0; index < scalarData.length; index++) {
    if (scalarData[index] === segmentIndex) voxelManager.setAtIndex(index, 0);
  }
  for (const index of snapshot.labelmapVoxelIndices) {
    voxelManager.setAtIndex(index, segmentIndex);
  }

  // Restore prior ownership only after geometry restoration succeeds.
  detachPredictionMaskSegment(state, predictionSegmentIndex);
  for (const key of snapshot.overlayLoadedKeys) state.loadedKeys.set(key, predictionSegmentIndex);
  for (const uid of snapshot.overlayAnnotationIds)
    state.annotationIds.set(uid, predictionSegmentIndex);
  if (!snapshot.wasDetached) state.detachedSegmentIndices.delete(predictionSegmentIndex);
}

/**
 * Whether the live geometry differs from the edit-entry checkpoint. Contours
 * cover Lasso/AI edits; labelmap voxels cover brush and eraser edits. Lock,
 * visibility and loaded-from-mask flags are ownership state, not geometry.
 */
export function predictionMaskEditChanged(snapshot: PredictionMaskEditSnapshot): boolean {
  const { segmentIndex, segmentationVolumeId } = snapshot;
  const live = getCornerstoneTools()
    .annotation.state.getAllAnnotations()
    .filter((ann) => {
      const segmentation = ann.data?.segmentation as { segmentIndex?: number } | undefined;
      return (
        ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
        segmentation?.segmentIndex === segmentIndex &&
        (ann.data?.contour?.polyline?.length ?? 0) >= 3
      );
    });
  if (live.length !== snapshot.contours.length) return true;
  const checkpoint = new Map(snapshot.contours.map((ann) => [ann.annotationUID, ann]));
  for (const ann of live) {
    const saved = checkpoint.get(ann.annotationUID);
    if (!saved || !samePolyline(ann.data?.contour?.polyline, saved.data?.contour?.polyline)) {
      return true;
    }
  }

  const scalarData = getCornerstone()
    .cache.getVolume(segmentationVolumeId)
    ?.voxelManager?.getCompleteScalarDataArray?.();
  if (!scalarData || scalarData.length !== snapshot.labelmapLength) {
    throw new Error("Prediction labelmap no longer matches the checkpoint");
  }
  // Both lists are ascending: the checkpoint was captured with the same scan.
  let cursor = 0;
  for (let index = 0; index < scalarData.length; index++) {
    if (scalarData[index] !== segmentIndex) continue;
    if (snapshot.labelmapVoxelIndices[cursor] !== index) return true;
    cursor++;
  }
  return cursor !== snapshot.labelmapVoxelIndices.length;
}

function samePolyline(a: readonly Point3[] | undefined, b: readonly Point3[] | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((point, i) => point.every((value, axis) => value === b[i][axis]));
}

/**
 * Return one contour per 4-connected island, traced along pixel edges and
 * smoothed like a reloaded saved mask (ENG-250). Rasterizing the contours
 * gives back exactly the model's pixels, so Edit → Save keeps the AI size.
 * Islands touching only at a corner become separate contours; Save unions them.
 */
export function extractPredictionMaskContours(
  bitmap: Uint8Array,
  rows: number,
  columns: number,
): Point2[][] {
  return extractContourEdges(bitmap, columns, rows).map((outline) => chaikinSmooth(outline, 2));
}

/**
 * Index of the viewer image holding a canonical object, or -1. Viewer imageIds
 * are `wadouri:<cdn>/<s3_key>`, optionally with a `?t=` CDN token. Match by
 * key, not by the SOP UID in the file name: some cohorts name files
 * `<study UID>.<n>.dcm` (ENG-269). Tries the metadata index first and searches
 * when the viewer holds the images in another order.
 */
export function findImageIndexForKey(
  imageIds: readonly string[],
  sliceIndex: number,
  s3Key: string | undefined,
): number {
  if (!s3Key) return -1;
  const holdsKey = (imageId: string | undefined) =>
    !!imageId && imageId.split("?", 1)[0].endsWith(`/${s3Key}`);
  return holdsKey(imageIds[sliceIndex]) ? sliceIndex : imageIds.findIndex(holdsKey);
}

/** Where each mask slice sits in the volume an Accept writes into. */
export interface MaskSliceVolumeIndices {
  /** Index of each slice's image in the volume's `imageIds`. */
  volumeIndex: Map<ResolvedPredictionMaskSlice, number>;
  /** Slices whose image is not in the volume (another series, ENG-278). */
  missing: ResolvedPredictionMaskSlice[];
}

/**
 * Each mask slice's index in `imageIds`, the annotation volume's images in its
 * own slice order (ENG-291). The labelmap is written in that order, the same
 * one Save uses (`toFileSlice`, ENG-258): the volume may list a series in
 * reverse of its metadata, so the metadata `sliceIndex` can be the mirrored
 * slice. "unknown" when that can't be checked (no image list, or a slice
 * without an object key); the caller then accepts by `sliceIndex` as before.
 */
export function maskSliceVolumeIndices(
  segment: Pick<ResolvedPredictionMaskSegment, "slices">,
  imageIds: readonly string[] | undefined,
): MaskSliceVolumeIndices | "unknown" {
  if (!imageIds?.length || segment.slices.some((slice) => !slice.s3Key)) return "unknown";
  const volumeIndex = new Map<ResolvedPredictionMaskSlice, number>();
  const missing: ResolvedPredictionMaskSlice[] = [];
  for (const slice of segment.slices) {
    const index = findImageIndexForKey(imageIds, slice.sliceIndex, slice.s3Key);
    if (index < 0) missing.push(slice);
    else volumeIndex.set(slice, index);
  }
  return { volumeIndex, missing };
}

/** One overlay pass. Pending slices are drawn on a later pass. */
export interface PredictionMaskOverlayPass {
  added: number;
  /** Matched slices whose DICOM header hasn't streamed yet. */
  pending: { imageId: string; segmentIndex: number }[];
  /** Segments with a slice whose file holds another SOP instance. */
  mismatchedSegmentIndices: Set<number>;
}

function positiveDimension(...values: unknown[]): number | undefined {
  return values.find(
    (value): value is number => typeof value === "number" && Number.isInteger(value) && value > 0,
  );
}

/**
 * Add every currently visible model-mask slice as a locked Cornerstone contour.
 * A slice is drawn only once its DICOM header has streamed: before that,
 * Cornerstone answers with the metadata IPP, which the backend may have
 * synthesized without the in-plane origin, and a contour placed there stays
 * off the image (ENG-269).
 */
export function addPredictionMaskContours(
  mask: ResolvedPredictionMask,
  segmentationVolumeId: string,
  state: PredictionMaskOverlayState,
  warn: (message: string, error?: unknown) => void,
  hiddenSegmentIndices: ReadonlySet<number> = new Set(),
): PredictionMaskOverlayPass {
  const cornerstone = getCornerstone();
  const csTools = getCornerstoneTools();
  const annotationState = csTools.annotation.state;
  const contourSegmentationId = getContourSegmentationId(segmentationVolumeId);
  const segmentIndices = mask.segments
    .map((segment) => segment.segmentIndex)
    .filter(
      (segmentIndex) =>
        !hiddenSegmentIndices.has(segmentIndex) && !state.detachedSegmentIndices.has(segmentIndex),
    );

  // Registering the prediction segments calls setActiveSegmentIndex for every
  // index. This overlay polls while the viewer is open, so without preserving
  // the current value it steals the active segment from an in-progress user
  // annotation (e.g. lasso draws segment 6, overlay switches to prediction 5,
  // then Bump cannot find the user's contour).
  const segmentIndexApi = csTools.segmentation.segmentIndex;
  const activeSegmentBeforeOverlay =
    segmentIndexApi?.getActiveSegmentIndex?.(contourSegmentationId);
  ensureContourSegmentationActive(segmentationVolumeId);
  initContourSegmentIndices(csTools.segmentation, contourSegmentationId, segmentIndices);
  segmentIndexApi?.setActiveSegmentIndex?.(contourSegmentationId, activeSegmentBeforeOverlay ?? 1);
  const contourType = csTools.Enums?.SegmentationRepresentations?.Contour;
  const segmentationStyle = csTools.segmentation?.config?.style;
  if (contourType && segmentationStyle?.setStyle) {
    for (const segmentIndex of segmentIndices) {
      segmentationStyle.setStyle(
        { type: contourType, segmentationId: contourSegmentationId, segmentIndex },
        {
          outlineWidth: 1,
          outlineOpacity: 1,
          renderOutline: true,
          outlineWidthInactive: 1,
          outlineOpacityInactive: 1,
          renderOutlineInactive: true,
        },
      );
    }
  }
  for (const engine of cornerstone.getRenderingEngines?.() ?? []) {
    for (const rawViewport of engine.getViewports?.() ?? []) {
      const viewport = rawViewport as any;
      for (const segmentIndex of segmentIndices) {
        try {
          setSegmentColor(
            csTools.segmentation.config,
            viewport.id,
            contourSegmentationId,
            segmentIndex,
            ACTIVE_SEGMENT_COLOR,
          );
        } catch {
          // A viewport may not have a contour representation yet; the next
          // render pass will apply the shared color after it is created.
        }
      }
    }
  }

  let added = 0;
  const pending: PredictionMaskOverlayPass["pending"] = [];
  const mismatchedSegmentIndices = new Set<number>();
  for (const engine of cornerstone.getRenderingEngines?.() ?? []) {
    for (const rawViewport of engine.getViewports?.() ?? []) {
      const viewport = rawViewport as any;
      const imageIds: string[] = viewport.getImageIds?.() ?? [];
      const element: HTMLElement | null = viewport.element ?? null;
      if (!element || imageIds.length === 0) continue;

      for (const segment of mask.segments) {
        if (
          hiddenSegmentIndices.has(segment.segmentIndex) ||
          state.detachedSegmentIndices.has(segment.segmentIndex)
        )
          continue;
        for (const slice of segment.slices) {
          // Drawn slices are skipped before the search: every poll runs this loop.
          const baseKey = predictionSliceKey(slice, segment.segmentIndex);
          if (state.loadedKeys.has(baseKey)) continue;
          const imageIndex = findImageIndexForKey(imageIds, slice.sliceIndex, slice.s3Key);
          if (imageIndex < 0) continue;
          const imageId = imageIds[imageIndex];

          // Only the wadouri provider sets sopCommonModule, so it marks a parsed header.
          const sopInstanceUid = cornerstone.metaData.get("sopCommonModule", imageId)
            ?.sopInstanceUID as string | undefined;
          if (!sopInstanceUid) {
            pending.push({ imageId, segmentIndex: segment.segmentIndex });
            continue;
          }
          if (sopInstanceUid !== slice.sliceId) {
            mismatchedSegmentIndices.add(segment.segmentIndex);
            if (!state.warnedKeys.has(baseKey)) {
              state.warnedKeys.add(baseKey);
              warn(
                `[predictionMask] ${slice.s3Key} holds SOP ${sopInstanceUid}, not mask slice ${slice.sliceId}; skipped.`,
              );
            }
            continue;
          }

          const ipm = cornerstone.metaData.get("imagePlaneModule", imageId);
          const ipp = ipm?.imagePositionPatient as number[] | undefined;
          const rowCos = ipm?.rowCosines as number[] | undefined;
          const columnCos = ipm?.columnCosines as number[] | undefined;
          const rowSpacing = ipm?.rowPixelSpacing as number | undefined;
          const columnSpacing = ipm?.columnPixelSpacing as number | undefined;
          const pixelModule = cornerstone.metaData.get("imagePixelModule", imageId);
          const imageDimensions = viewport.getImageData?.()?.imageData?.getDimensions?.();
          const targetRows = positiveDimension(
            ipm?.rows,
            pixelModule?.rows,
            imageDimensions?.[1],
            mask.rows,
          )!;
          const targetColumns = positiveDimension(
            ipm?.columns,
            pixelModule?.columns,
            imageDimensions?.[0],
            mask.columns,
          )!;
          if (!ipp || !rowCos || !columnCos || !rowSpacing || !columnSpacing) {
            warn(`[predictionMask] missing DICOM plane geometry for ${slice.sliceId}; skipped.`);
            continue;
          }

          const bitmap = decodePredictionMaskRuns(mask.rows, mask.columns, slice.runs);
          const contours = extractPredictionMaskContours(bitmap, mask.rows, mask.columns);
          const camera = viewport.getCamera?.() ?? {};
          const frameOfReferenceUid = viewport.getFrameOfReferenceUID?.();

          contours.forEach((contour, contourIndex) => {
            const worldPoints: Point3[] = contour.map(([sourceRow, sourceColumn]) => {
              // The model is fixed at 512² while acquisitions may not be. Scale
              // at pixel centers into the native grid before applying DICOM IPP.
              const row = ((sourceRow + 0.5) * targetRows) / mask.rows - 0.5;
              const column = ((sourceColumn + 0.5) * targetColumns) / mask.columns - 0.5;
              return [
                ipp[0] + column * columnSpacing * rowCos[0] + row * rowSpacing * columnCos[0],
                ipp[1] + column * columnSpacing * rowCos[1] + row * rowSpacing * columnCos[1],
                ipp[2] + column * columnSpacing * rowCos[2] + row * rowSpacing * columnCos[2],
              ];
            });
            const annotationUid =
              cornerstone.utilities.uuidv4?.() ??
              `prediction-mask-${segment.segmentIndex}-${slice.sliceIndex}-${contourIndex}`;
            const annotation = {
              annotationUID: annotationUid,
              metadata: {
                toolName: "PlanarFreehandContourSegmentationTool",
                referencedImageId: imageId,
                FrameOfReferenceUID: frameOfReferenceUid,
                viewPlaneNormal: [...(camera.viewPlaneNormal ?? [0, 0, 1])],
                viewUp: [...(camera.viewUp ?? [0, -1, 0])],
                sliceIndex: slice.sliceIndex,
              },
              data: {
                contour: { polyline: worldPoints, closed: true },
                segmentation: {
                  segmentationId: contourSegmentationId,
                  segmentIndex: segment.segmentIndex,
                },
                handles: { points: [], activeHandleIndex: null },
              },
              highlighted: false,
              isLocked: true,
              isVisible: true,
              invalidated: false,
              [LOADED_FROM_MASK_KEY]: true,
              _predictionMask: true,
            };
            try {
              annotationState.addAnnotation(annotation as any, element as any);
              csTools.utilities.contourSegmentation.addContourSegmentationAnnotation(
                annotation as any,
              );
              state.annotationIds.set(annotationUid, segment.segmentIndex);
              added++;
            } catch (error) {
              warn(`[predictionMask] failed to register contour ${baseKey}.`, error);
            }
          });
          state.loadedKeys.set(baseKey, segment.segmentIndex);
          viewport.render?.();
        }
      }
    }
  }
  return { added, pending, mismatchedSegmentIndices };
}

/** `loadedKeys` key of one prediction slice. */
export function predictionSliceKey(
  slice: Pick<ResolvedPredictionMaskSlice, "seriesId" | "sliceId">,
  segmentIndex: number,
): string {
  return `${slice.seriesId}:${slice.sliceId}:${segmentIndex}`;
}

/** True only while every prediction contour registered by this overlay exists. */
export function predictionMaskContoursArePresent(state: PredictionMaskOverlayState): boolean {
  if (state.annotationIds.size === 0) return false;
  try {
    const annotationState = getCornerstoneTools().annotation.state;
    return [...state.annotationIds.keys()].every((uid) => !!annotationState.getAnnotation?.(uid));
  } catch {
    return false;
  }
}

export function removePredictionMaskContours(state: PredictionMaskOverlayState): void {
  try {
    // React Strict Mode runs effect cleanup once during the initial mount. At
    // that point Cornerstone may still be loading and there cannot be any mask
    // annotations to unregister yet, so avoid touching the uninitialized tools
    // singleton.
    if (state.annotationIds.size === 0) return;

    const csTools = getCornerstoneTools();
    const unregister = csTools.utilities?.contourSegmentation?.removeContourSegmentationAnnotation;
    for (const annotationUid of state.annotationIds.keys()) {
      const annotation = csTools.annotation.state.getAnnotation?.(annotationUid);
      // Clear Cornerstone's global hidden-id registry before deleting a
      // declined contour. Otherwise remounts leave an unreachable UUID in the
      // registry even though the annotation itself is gone.
      try {
        csTools.annotation.visibility?.setAnnotationVisibility?.(annotationUid, true);
      } catch {
        // Visibility cleanup is best effort; state removal below is authoritative.
      }
      try {
        if (annotation && unregister) unregister(annotation as any);
      } catch {
        // State removal below is authoritative; unregister is best effort.
      }
      try {
        csTools.annotation.state.removeAnnotation(annotationUid);
      } catch {
        // Cornerstone may already have cleared global annotation state.
      }
    }
    // Removing an annotation does not repaint. Without this, hidden or
    // declined contours stay drawn until an unrelated render (e.g. a scroll).
    for (const engine of getCornerstone().getRenderingEngines?.() ?? []) {
      try {
        engine.render();
      } catch {
        // A torn-down engine has nothing left to repaint.
      }
    }
  } finally {
    state.annotationIds.clear();
    state.loadedKeys.clear();
    // Recovery clears only overlay tracking, not detached ownership. Otherwise
    // the next render could recreate raw predictions over the editor's result.
  }
}
